import { useState, useRef, useEffect, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { MUSIC_TRACKS } from '../data/camp-data';

export default function MusicPlayer({ currentTrack = 'landing', paused = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [userMuted, setUserMuted] = useState(false);
  const audioRef = useRef(null);
  const prevTrackRef = useRef(currentTrack);
  const startedRef = useRef(false);

  const track = MUSIC_TRACKS[currentTrack] || MUSIC_TRACKS.landing;

  const tryPlay = useCallback(() => {
    if (!audioRef.current || startedRef.current || paused || userMuted) return;
    audioRef.current.volume = 0.4;
    if (track.startAt && audioRef.current.currentTime < track.startAt) {
      audioRef.current.currentTime = track.startAt;
    }
    audioRef.current.play()
      .then(() => {
        startedRef.current = true;
        setIsPlaying(true);
      })
      .catch(() => {});
  }, [paused, userMuted, track.startAt]);

  // Try autoplay on mount
  useEffect(() => {
    tryPlay();
  }, [tryPlay]);

  // Listen for ANY user interaction to start music
  useEffect(() => {
    if (startedRef.current) return;

    const handler = () => {
      if (!startedRef.current && !paused) {
        tryPlay();
      }
    };

    const events = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    events.forEach(e => document.addEventListener(e, handler, { once: false, passive: true }));

    return () => {
      events.forEach(e => document.removeEventListener(e, handler));
    };
  }, [tryPlay, paused]);

  // Pause/resume based on prop
  useEffect(() => {
    if (!audioRef.current) return;
    if (paused || userMuted) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else if (!paused && !userMuted && startedRef.current) {
      audioRef.current.volume = 0.4;
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [paused, userMuted]);

  // Switch tracks
  useEffect(() => {
    if (prevTrackRef.current !== currentTrack && audioRef.current) {
      prevTrackRef.current = currentTrack;
      const newTrack = MUSIC_TRACKS[currentTrack] || MUSIC_TRACKS.landing;
      const wasPlaying = startedRef.current && !paused;
      audioRef.current.pause();
      audioRef.current.src = newTrack.url;
      audioRef.current.load();
      if (wasPlaying) {
        audioRef.current.volume = 0.4;
        const onCanPlay = () => {
          if (newTrack.startAt) audioRef.current.currentTime = newTrack.startAt;
          audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          audioRef.current.removeEventListener('canplay', onCanPlay);
        };
        audioRef.current.addEventListener('canplay', onCanPlay);
      }
    }
  }, [currentTrack, paused]);

  const toggleMute = () => {
    if (userMuted) {
      setUserMuted(false);
      if (audioRef.current && !paused) {
        audioRef.current.volume = 0.4;
        audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    } else {
      setUserMuted(true);
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  return (
    <div className="fixed bottom-6 left-6 z-50 flex items-center gap-3" data-testid="music-player">
      <audio ref={audioRef} src={track.url} loop preload="auto" />

      {isPlaying && (
        <div className="glass rounded-full px-4 py-2 text-xs text-[var(--mc-text-sec)] font-mono hidden sm:block">
          {track.name}
        </div>
      )}

      {isPlaying && (
        <div className="flex items-end gap-[2px] h-7">
          <div className="w-[3px] bg-[var(--mc-pink)] rounded-full viz-bar-1" />
          <div className="w-[3px] bg-[var(--mc-cyan)] rounded-full viz-bar-2" />
          <div className="w-[3px] bg-[var(--mc-pink)] rounded-full viz-bar-3" />
          <div className="w-[3px] bg-[var(--mc-cyan)] rounded-full viz-bar-4" />
        </div>
      )}

      <button
        onClick={toggleMute}
        data-testid="mute-toggle-button"
        className="w-10 h-10 rounded-full glass flex items-center justify-center glow-border-pink
                   hover:scale-110 transition-transform cursor-pointer"
      >
        {isPlaying ? (
          <Volume2 className="w-4 h-4 text-[var(--mc-pink)]" />
        ) : (
          <VolumeX className="w-4 h-4 text-[var(--mc-text-muted)]" />
        )}
      </button>
    </div>
  );
}
